import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AlertCircle, Plus, RotateCcw, Search, Loader2 } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { createDevolution, getLastPixKey } from "@/Api/devolution";
import { getSelectClients } from "@/Api/clients";
import { getPixOperatorConfig } from "@/Api/configTenant";
import { Mask } from "@/utils/mask";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  PIX_KEY_TYPES,
  PixKeyType,
  maskPixKey,
  parsePixKey,
  toPixKeyValue,
  validatePixKey,
  isValidCpf,
  isValidCnpj,
} from "@/utils/pixKey";

const devolutionSchema = z.object({
  name: z.string().optional(),
  value: z.number().min(0.01, "Valor deve ser maior que zero"),
  client_id: z.number().min(1, "Cliente é obrigatório"),
  pix_key: z.string().min(1, "Chave PIX é obrigatória"),
});

type DevolutionForm = z.infer<typeof devolutionSchema>;

interface Client {
  id: number;
  name: string;
  email?: string;
  document?: string | null;
}

interface AddDevolutionProps {
  updateFront: any;
  preSelectedClientId?: number;
  preSelectedClientName?: string;
  dataApi?: any;
  refreshList?: () => Promise<void>;
}

export function AddDevolution({
  updateFront,
  refreshList,
  preSelectedClientId,
  preSelectedClientName,
}: AddDevolutionProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  // CPF do cliente: vem do cadastro e, se alterado aqui, atualiza o cadastro do cliente
  const [clientDocument, setClientDocument] = useState("");
  const hasClient = !!selectedClient || !!preSelectedClientId;
  const documentDigits = clientDocument.replace(/\D/g, "");
  const documentError =
    documentDigits === ""
      ? "CPF não cadastrado. Preencha para criar a devolução."
      : documentDigits.length === 11
        ? isValidCpf(documentDigits) ? null : "CPF inválido."
        : documentDigits.length === 14
          ? isValidCnpj(documentDigits) ? null : "CNPJ inválido."
          : "CPF inválido.";
  // Operadora ativa em "Pagamentos / Transferência PIX - (Devoluções)"
  const [payoutOperator, setPayoutOperator] = useState<
    "btg" | "sicredi" | "none" | null
  >(null);
  const [pixKeyType, setPixKeyType] = useState<PixKeyType>("cpf");
  const [pixKeyInput, setPixKeyInput] = useState("");
  // true quando a chave veio da última devolução do cliente (continua editável)
  const [pixKeyPrefilled, setPixKeyPrefilled] = useState(false);
  const pixKeyInputRef = useRef("");
  pixKeyInputRef.current = pixKeyInput;
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    formData: DevolutionForm | null;
  }>({
    isOpen: false,
    formData: null,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    setError,
    clearErrors,
  } = useForm<DevolutionForm>({
    resolver: zodResolver(devolutionSchema),
    defaultValues: {
      client_id: preSelectedClientId || 0,
    },
  });

  useEffect(() => {
    if (preSelectedClientId) {
      setValue("client_id", preSelectedClientId);
    }
  }, [preSelectedClientId, setValue]);

  useEffect(() => {
    if (!isOpen) return;

    getPixOperatorConfig().then(
      ({ pixPayoutBtgActive, pixPayoutSicrediActive }) =>
        setPayoutOperator(
          pixPayoutBtgActive && !pixPayoutSicrediActive
            ? "btg"
            : pixPayoutSicrediActive
            ? "sicredi"
            : "none"
        )
    );
  }, [isOpen]);

  // Carregar lista inicial quando abrir o modal
  useEffect(() => {
    const loadInitialClients = async () => {
      if (!isOpen || preSelectedClientId) return;

      setIsSearching(true);
      try {
        const response = await getSelectClients();
        if (response.data?.response) {
          setClients(response.data.response);
        }
      } catch (error) {
        console.error("Erro ao carregar clientes:", error);
      } finally {
        setIsSearching(false);
      }
    };

    loadInitialClients();
  }, [isOpen, preSelectedClientId]);

  // Debounce search
  useEffect(() => {
    if (!isOpen || preSelectedClientId) return;

    const timeoutId = setTimeout(async () => {
      setIsSearching(true);
      try {
        const response = await getSelectClients(searchTerm ? { name: searchTerm } : undefined);
        if (response.data?.response) {
          setClients(response.data.response);
        }
      } catch (error) {
        console.error("Erro ao buscar clientes:", error);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, isOpen, preSelectedClientId]);

  // Traz a chave da última devolução do cliente, sem sobrescrever o que a pessoa já digitou
  const prefillPixKey = async (clientId: number) => {
    const res: any = await getLastPixKey(clientId);
    const parsed = parsePixKey(res?.data?.response?.pix_key);

    if (!res?.ok || !parsed || pixKeyInputRef.current) return;

    setPixKeyType(parsed.type);
    setPixKeyInput(parsed.display);
    setValue("pix_key", toPixKeyValue(parsed.type, parsed.display));
    clearErrors("pix_key");
    setPixKeyPrefilled(true);
  };

  useEffect(() => {
    if (isOpen && preSelectedClientId) {
      prefillPixKey(preSelectedClientId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, preSelectedClientId]);

  // Cliente pré-selecionado (vindo da lista de clientes): busca o CPF no cadastro
  useEffect(() => {
    if (!isOpen || !preSelectedClientId || !preSelectedClientName) return;

    getSelectClients({ name: preSelectedClientName }).then((res: any) => {
      const found = (res?.data?.response || []).find(
        (c: Client) => c.id === preSelectedClientId
      );
      setClientDocument(Mask.maskCpfOrCnpj(found?.document || ""));
    });
  }, [isOpen, preSelectedClientId, preSelectedClientName]);

  const handleSelectClient = (client: Client) => {
    setClientDocument(Mask.maskCpfOrCnpj(client.document || ""));
    setSelectedClient(client);
    setValue("client_id", client.id);
    setSearchTerm("");
    prefillPixKey(client.id);
  };

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const numericValue = Number(value.replace(/\D/g, "")) / 100;
    setValue("value", numericValue);
  };

  const handlePixKeyTypeChange = (type: PixKeyType) => {
    setPixKeyType(type);
    setPixKeyPrefilled(false);
    setPixKeyInput("");
    setValue("pix_key", "");
    clearErrors("pix_key");
  };

  const handlePixKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const masked = maskPixKey(pixKeyType, e.target.value);
    setPixKeyPrefilled(false);
    setPixKeyInput(masked);
    setValue("pix_key", masked ? toPixKeyValue(pixKeyType, masked) : "");
    clearErrors("pix_key");
  };

  const onSubmit = (data: DevolutionForm) => {
    if (documentError) return;

    const pixKeyError = validatePixKey(pixKeyType, pixKeyInput);
    if (pixKeyError) {
      setError("pix_key", { type: "manual", message: pixKeyError });
      return;
    }

    // Abrir modal de confirmação com os dados
    setConfirmModal({
      isOpen: true,
      formData: data,
    });
  };

  const handleConfirmSubmit = async () => {
    if (!confirmModal.formData) return;

    setIsLoading(true);
    try {
      const response = await createDevolution({
        ...confirmModal.formData,
        client_document: documentDigits,
      });
      if (!response.ok) return;
      toast.success("Devolução criada com sucesso!");

      reset();
      setPixKeyType("cpf");
      setPixKeyPrefilled(false);
      setPixKeyInput("");
      setIsOpen(false);
      setConfirmModal({ isOpen: false, formData: null });

      // Se for chamado da listagem de clientes, refresh a página de clientes
      if (preSelectedClientId && refreshList) {
        await refreshList();
        return;
      }

      // Se for da página de devoluções, atualiza a lista local
      if (!preSelectedClientId) {
        updateFront((prev: any) => ({
          ...prev,
          data: [response.data?.response, ...prev.data],
        }));
      }
    } catch (error) {
      toast.error("Erro ao criar devolução");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      reset();
      setPixKeyType("cpf");
      setPixKeyPrefilled(false);
      setPixKeyInput("");
      setConfirmModal({ isOpen: false, formData: null });
      setSearchTerm("");
      setSelectedClient(null);
      setClientDocument("");
      setClients([]);
    }
  };

  const getSelectedClientName = () => {
    if (preSelectedClientName) return preSelectedClientName;
    if (selectedClient) return selectedClient.name;
    return "Cliente não encontrado";
  };

  return (
    <>
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {preSelectedClientId ? (
          <Button variant="outline" size="icon" className="h-8 w-8">
            <RotateCcw className="h-4 w-4" />
          </Button>
        ) : (
          <Button className="bg-primary hover:bg-primary/90">
            <Plus className="mr-2 h-4 w-4" />
            Nova Devolução
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nova Devolução</DialogTitle>
          <DialogDescription>
            Crie uma nova solicitação de devolução para o cliente.
          </DialogDescription>
        </DialogHeader>
        {payoutOperator && (
          <div className="flex items-center gap-3 rounded-lg border p-3">
            <div
              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-bold text-white ${
                payoutOperator === "btg"
                  ? "bg-blue-600"
                  : payoutOperator === "sicredi"
                  ? "bg-green-600"
                  : "bg-gray-500"
              }`}
            >
              {payoutOperator === "btg" ? "B" : payoutOperator === "sicredi" ? "S" : "-"}
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold">
                  {payoutOperator === "btg"
                    ? "PIX BTG Pactual"
                    : payoutOperator === "sicredi"
                    ? "PIX Sicredi"
                    : "Nenhuma operadora ativa"}
                </span>
                {payoutOperator !== "none" && (
                  <Badge className="bg-green-500 text-white">Ativo</Badge>
                )}
              </div>
              {payoutOperator === "btg" ? (
                <p className="text-xs text-muted-foreground">
                  Operadora de pagamento. Ao aprovar, o PIX é criado no BTG.
                </p>
              ) : (
                <div
                  role="alert"
                  className="mt-1 flex items-start gap-1.5 rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-xs font-medium text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200"
                >
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                  <span>
                    Operadora de pagamento. Sem envio automático: o pagamento é
                    feito manualmente.
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="client_id">Cliente</Label>
            {preSelectedClientId && preSelectedClientName ? (
              <Input
                value={preSelectedClientName}
                disabled
                className="bg-muted"
              />
            ) : (
              <div className="space-y-2">
                {selectedClient ? (
                  <div className="flex items-center justify-between p-2 border rounded-md bg-muted">
                    <span>{selectedClient.name}</span>
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => {
                        setSelectedClient(null);
                        setClientDocument("");
                        setValue("client_id", 0);
                      }}
                      className="h-6 px-2 text-xs bg-primary text-primary-foreground hover:bg-primary/90"
                    >
                      Alterar
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <Input
                        placeholder="Buscar cliente pelo nome..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                      />
                      {isSearching && (
                        <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 animate-spin text-gray-400" />
                      )}
                    </div>
                    <div className="border rounded-md max-h-40 overflow-auto">
                      {clients.length > 0 ? (
                        clients.map((client) => (
                          <button
                            key={client.id}
                            type="button"
                            className="w-full px-3 py-2 text-left hover:bg-gray-100 text-sm border-b last:border-b-0"
                            onClick={() => handleSelectClient(client)}
                          >
                            {client.name}
                          </button>
                        ))
                      ) : (
                        <div className="p-3 text-sm text-gray-500 text-center">
                          {isSearching ? "Buscando..." : "Nenhum cliente encontrado"}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
            {errors.client_id && (
              <p className="text-sm text-red-500">{errors.client_id.message}</p>
            )}
          </div>

          {hasClient && (
            <div className="space-y-2">
              <Label htmlFor="client_document">CPF do cliente</Label>
              {documentError && (
                <div
                  role="alert"
                  className="flex items-start gap-1.5 rounded border border-red-300 bg-red-50 px-2 py-1.5 text-xs font-medium text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200"
                >
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
                  <p>{documentError}</p>
                </div>
              )}
              <Input
                id="client_document"
                value={clientDocument}
                onChange={(e) => setClientDocument(Mask.maskCpfOrCnpj(e.target.value))}
                inputMode="numeric"
                placeholder="000.000.000-00"
                aria-invalid={!!documentError}
                className={
                  documentError
                    ? "border-red-500 text-red-700 focus-visible:ring-red-500"
                    : ""
                }
              />
              <div
                role="alert"
                className={`flex items-start gap-1.5 rounded border px-2 py-1.5 text-xs font-medium ${
                  documentError
                    ? "border-red-300 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200"
                    : "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200"
                }`}
              >
                <AlertCircle
                  className={`mt-0.5 h-4 w-4 shrink-0 ${
                    documentError ? "text-red-600" : "text-amber-600"
                  }`}
                />
                <p>
                  ALERTA: Você confirma que o CPF é o do mesmo cliente que comprou a aposta!
                </p>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="name">Descrição</Label>
            <Textarea
              {...register("name")}
              placeholder="Descreva o motivo da devolução..."
              className="min-h-[80px]"
            />
            {errors.name && (
              <p className="text-sm text-red-500">{errors.name.message}</p>
            )}
          </div>

           <div className="space-y-2">
            <Label htmlFor="pix_key">Chave PIX</Label>
            <Select
              value={pixKeyType}
              onValueChange={(v) => handlePixKeyTypeChange(v as PixKeyType)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Tipo da chave" />
              </SelectTrigger>
              <SelectContent>
                {PIX_KEY_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              id="pix_key"
              value={pixKeyInput}
              onChange={handlePixKeyChange}
              inputMode={
                pixKeyType === "email" || pixKeyType === "random"
                  ? "text"
                  : "numeric"
              }
              placeholder={
                PIX_KEY_TYPES.find((t) => t.value === pixKeyType)?.placeholder
              }
            />
            {pixKeyPrefilled && (
              <p className="text-xs text-muted-foreground">
                Chave da última devolução deste cliente. Você pode editar.
              </p>
            )}
            {errors.pix_key && (
              <p className="text-sm text-red-500">{errors.pix_key.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="value">Valor</Label>
            <Input
              typeMask="currency"
              onChange={handleValueChange}
              placeholder="R$ 0,00"
            />
            {errors.value && (
              <p className="text-sm text-red-500">{errors.value.message}</p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={isLoading || (hasClient && !!documentError)}
              className="bg-primary hover:bg-primary/90"
            >
              {isLoading ? "Criando..." : "Criar Devolução"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>

    <Dialog 
      open={confirmModal.isOpen} 
      onOpenChange={(open) => setConfirmModal({ isOpen: open, formData: null })}
    >
      <DialogContent className="sm:max-w-[425px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Confirmar Criação de Devolução</DialogTitle>
          <DialogDescription>
            Tem certeza que deseja criar esta devolução?
          </DialogDescription>
        </DialogHeader>

        {confirmModal.formData && (
          <div className="space-y-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-800 mb-2">
                Resumo da Devolução
              </h4>
              <div className="grid grid-cols-1 gap-2 text-sm">
                <div>
                  <span className="font-medium">Cliente:</span>
                  <p>{getSelectedClientName()}</p>
                </div>
                <div>
                  <span className="font-medium">Descrição:</span>
                  <p>{confirmModal.formData.name || "Não informada"}</p>
                </div>
                <div>
                  <span className="font-medium">Valor:</span>
                  <p className="text-lg font-semibold text-green-600">
                    {Mask.formatCurrency(confirmModal.formData.value)}
                  </p>
                </div>
                <div>
                  <span className="font-medium">
                    Chave PIX (
                    {PIX_KEY_TYPES.find((t) => t.value === pixKeyType)?.label}
                    ):
                  </span>
                  <p className="font-mono text-sm break-all">
                    {pixKeyInput}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => setConfirmModal({ isOpen: false, formData: null })}
            disabled={isLoading}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmSubmit}
            disabled={isLoading}
            className="bg-primary hover:bg-primary/90"
          >
            {isLoading ? "Criando..." : "Confirmar e Criar Devolução"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>
  );
}
